import type { ReactNode } from 'react'
import { useRef, useEffect, useCallback, useState } from 'react'
import { PlaceMedallion } from '@/components/place/PlaceMedallion'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { VaultDraftCard } from './VaultDraftCard'
import { ChatHistoryMenu } from './ChatHistoryMenu'
import type { ChatMessage as ChatMessageType, EntityContext, ChatMode, ChatSession } from '@/types/chat'
import type { FamilyMember } from '@/types/family'
import type { ChatAttachment } from './ChatAttachment'

interface ChatPanelProps {
  voiceControls?: ReactNode
  /** Shown above the message field, outside the scrolling transcript (the
   *  change card waiting for Approve). */
  beforeInput?: ReactNode
  messages: ChatMessageType[]
  loading: boolean
  error: string | null
  entityContext: EntityContext | null
  onSend: (message: string, attachment?: ChatAttachment) => void
  onClear: () => void
  onClose: () => void
  onSourceClick?: (noteId: string) => void
  onSaveToVault?: (title: string, content: string) => Promise<boolean>
  onAddTask?: (title: string, destination: 'inbox' | 'today') => void
  toolActivity?: string[]
  mode?: ChatMode
  /** Tappable starter prompts shown in the empty state; click sends them. */
  suggestions?: string[]
  // Chat history
  sessions?: ChatSession[]
  sessionsLoading?: boolean
  onLoadSession?: (session: ChatSession) => void
  onDeleteSession?: (sessionId: string) => void
  onNewChat?: () => void
  activeSessionId?: string | null
  // Shared (Discuss) threads
  /** Title shown in the header instead of "Symphony AI". */
  heading?: string
  /** The viewer's auth user id — decides which bubbles are "mine". */
  currentUserId?: string | null
  /** Faces for the authors in a shared thread, matched on auth_user_id. */
  familyMembers?: FamilyMember[]
  /** Names in the shared thread; rendered as avatars in the header. */
  participants?: string[]
}

function initialsFrom(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
}


export function ChatPanel({
  voiceControls,
  beforeInput,
  messages,
  loading,
  error,
  entityContext,
  onSend,
  onClear,
  onClose,
  onSourceClick,
  onSaveToVault,
  onAddTask,
  toolActivity,
  mode = 'chat',
  suggestions,
  sessions = [],
  sessionsLoading = false,
  onLoadSession,
  onDeleteSession,
  onNewChat,
  activeSessionId,
  heading,
  currentUserId,
  familyMembers = [],
  participants = [],
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [dismissedDrafts, setDismissedDrafts] = useState<Set<string>>(new Set())

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])


  const placeholder = entityContext
    ? `Ask about ${entityContext.name}...`
    : 'Ask Symphony anything...'

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
  }, [onClose])

  return (
    <div
      className="relative flex flex-col h-full min-h-0 bg-white border-l border-neutral-200"
      onKeyDown={handleKeyDown}
    >
      {/* No place wash: a strong tinted gradient down the assistant column
          fought the paper-white work beside it (parity pass 2026-09-17). The
          header's own mark says whose pane this is. */}
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-[15px] font-medium text-neutral-800">
              {heading ?? (mode === 'guided_reflection' ? 'Think It Through' : 'Symphony AI')}
            </h3>
            {entityContext && (
              <p className="text-[11px] text-neutral-400">
                {entityContext.name}
              </p>
            )}
          </div>
          {/* Who is in this conversation — a shared thread only */}
          {participants.length > 0 && (
            <div className="flex items-center -space-x-1 ml-1" aria-label="Participants">
              {participants.map((name) => (
                <span
                  key={name}
                  title={name}
                  className="w-5 h-5 rounded-full bg-neutral-200 text-neutral-600 text-[9px] font-semibold flex items-center justify-center ring-1 ring-white"
                >
                  {initialsFrom(name)}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* New chat button */}
          {messages.length > 0 && onNewChat && (
            <button
              onClick={onNewChat}
              className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
              title="New chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          {/* History button */}
          {onLoadSession && (
            <ChatHistoryMenu sessions={sessions} loading={sessionsLoading} activeSessionId={activeSessionId}
              onLoad={onLoadSession} onDelete={onDeleteSession} onNew={onNewChat} />
          )}
          {/* Clear button */}
          {messages.length > 0 && (
            <button
              onClick={onClear}
              className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
              title="Clear chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          <button
            onClick={onClose}
            className="panel-close p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
            title="Close chat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {voiceControls}
      {/* Messages area */}
      <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="relative flex flex-col items-center justify-center h-full text-center">
            {/* The blank slate is the largest surface in this pane, and it was
                the one place the Place didn't reach: a generic question-mark
                glyph over an empty white column. The user's own medallion says
                the same "nothing here yet" and belongs to the room it's in. */}
            <div className="chat-place-medallion mb-4 h-20 w-20 overflow-hidden rounded-full shadow-sm ring-1 ring-neutral-200/70">
              <PlaceMedallion className="h-full w-full" />
            </div>
            <p className="text-sm text-neutral-500 mb-1">
              {mode === 'guided_reflection' && entityContext
                ? `Let's think through ${entityContext.name}`
                : entityContext
                ? `Ask about ${entityContext.name}`
                : 'Ask me anything'
              }
            </p>
            <p className="text-xs text-neutral-400">
              {mode === 'guided_reflection'
                ? "I'll ask questions to help you explore this — save the result if it's worth keeping"
                : "I can manage your tasks, plans, and calendar in Symphony"
              }
            </p>
            {suggestions && suggestions.length > 0 && (
              <div className="mt-4 flex flex-col gap-2 w-full max-w-[260px]">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onSend(s)}
                    className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200 transition-colors text-left"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id}>
              <ChatMessage
                message={msg}
                onSourceClick={onSourceClick}
                onAddTask={onAddTask}
                currentUserId={currentUserId}
                familyMembers={familyMembers}
              />
              {msg.vaultDraft && onSaveToVault && !dismissedDrafts.has(msg.id) && (
                <VaultDraftCard
                  title={msg.vaultDraft.title}
                  content={msg.vaultDraft.content}
                  onSave={(title, content) => onSaveToVault(title, content)}
                  onDismiss={() => setDismissedDrafts(prev => new Set([...prev, msg.id]))}
                />
              )}
            </div>
          ))
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-neutral-100 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-auto max-w-[85%] rounded-lg bg-red-50 text-red-700 text-xs px-3 py-2 mb-3">
            {error}
          </div>
        )}
      </div>

      {/* Tool activity */}
      {toolActivity && toolActivity.length > 0 && loading && (
        <div className="relative px-4 py-1.5 text-xs text-neutral-400 border-t border-neutral-200/60">
          {toolActivity[toolActivity.length - 1].replace(/^mcp__symphony__symphony_/, '').replace(/^symphony_/, '').replace(/_/g, ' ')}…
        </div>
      )}

      {beforeInput && <div className="relative max-h-[60%] shrink-0 overflow-y-auto px-3 pt-2">{beforeInput}</div>}

      {/* Input */}
      <ChatInput
        onSend={(msg, attachment) => onSend(msg, attachment)}
        loading={loading}
        placeholder={placeholder}
      />
    </div>
  )
}
